import React, { useEffect } from 'react';
import { useSite } from '../../context/SiteContext';
import Header from '../layout/Header';
import Footer from '../layout/Footer';
import { PromoBar, NoticeBar } from '../layout/Bars';
import PricingSection from './PricingSection';
import ContentSection from './ContentSection';
import ReviewsSection from './ReviewsSection';
import FloatingProof from './FloatingProof';
import MobileStickyCTA from './MobileStickyCTA';

export const HomePage = () => {
  const { settings, loading, error } = useSite();

  // Set document head
  useEffect(() => {
    if (settings) {
      document.title = settings.meta_title || 'Boostaro - Official Website';
      
      // Meta description
      let metaDesc = document.querySelector('meta[name="description"]');
      if (!metaDesc) {
        metaDesc = document.createElement('meta');
        metaDesc.name = 'description';
        document.head.appendChild(metaDesc);
      }
      metaDesc.content = settings.meta_description || '';
      
      // Meta keywords
      let metaKeywords = document.querySelector('meta[name="keywords"]');
      if (!metaKeywords) {
        metaKeywords = document.createElement('meta');
        metaKeywords.name = 'keywords';
        document.head.appendChild(metaKeywords);
      }
      metaKeywords.content = settings.meta_keywords || '';
      
      // OG tags
      const setOgTag = (property, content) => {
        let tag = document.querySelector(`meta[property="${property}"]`);
        if (!tag) {
          tag = document.createElement('meta');
          tag.setAttribute('property', property);
          document.head.appendChild(tag);
        }
        tag.content = content || '';
      };
      
      setOgTag('og:title', settings.meta_title);
      setOgTag('og:description', settings.meta_description);
      setOgTag('og:type', 'website');
      if (settings.og_image) setOgTag('og:image', settings.og_image);
      
      // Twitter Card
      const setTwitterTag = (name, content) => {
        let tag = document.querySelector(`meta[name="${name}"]`);
        if (!tag) {
          tag = document.createElement('meta');
          tag.name = name;
          document.head.appendChild(tag);
        }
        tag.content = content || '';
      };
      
      setTwitterTag('twitter:card', settings.twitter_card || 'summary_large_image');
      setTwitterTag('twitter:title', settings.meta_title);
      setTwitterTag('twitter:description', settings.meta_description);
      
      // Canonical URL
      let canonical = document.querySelector('link[rel="canonical"]');
      if (!canonical) {
        canonical = document.createElement('link');
        canonical.rel = 'canonical';
        document.head.appendChild(canonical);
      }
      canonical.href = window.location.origin + window.location.pathname;

      // Favicon
      if (settings.favicon_url) {
        let favicon = document.querySelector('link[rel="icon"]');
        if (!favicon) {
          favicon = document.createElement('link');
          favicon.rel = 'icon';
          document.head.appendChild(favicon);
        }
        favicon.href = settings.favicon_url;
      }
      
      // Schema.org JSON-LD
      let schemaScript = document.querySelector('script[type="application/ld+json"]');
      if (!schemaScript) {
        schemaScript = document.createElement('script');
        schemaScript.type = 'application/ld+json';
        document.head.appendChild(schemaScript);
      }
      const schemaData = settings.schema_data || {
        '@context': 'https://schema.org',
        '@type': 'Product',
        name: settings.site_name || 'Boostaro',
        description: settings.meta_description,
      };
      schemaScript.textContent = JSON.stringify(schemaData);
    }
  }, [settings]);

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-white">
        <div className="text-center">
          <div className="animate-spin rounded-full h-16 w-16 border-4 border-slate-200 border-t-blue-600 mx-auto" />
          <p className="mt-4 text-slate-600 font-medium">Loading...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-white">
        <div className="text-center">
          <p className="text-red-600 font-medium">Failed to load site data</p>
          <button
            onClick={() => window.location.reload()}
            className="mt-4 btn-cta px-6 py-2 rounded-full font-bold"
          >
            Retry
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-white" data-testid="home-page">
      <PromoBar />
      <Header />
      <NoticeBar />
      
      <main className="pt-8">
        <PricingSection />
        <ContentSection />
        <ReviewsSection />
      </main>
      
      <Footer />
      <FloatingProof />
      <MobileStickyCTA />
    </div>
  );
};

export default HomePage;
